// libs
import React, { memo, useState, useEffect } from 'react';

//components
import {
  WishlistIcon,
  WishlistButton,
  Container,
  LoginNudgeContainer,
} from 'shared/components/Wishlist/styles';
import LoginNudge from 'shared/components/OTPVerification/LoginNudge';
import Toast from 'shared/components/Toast/components/Toast';
import Portal from 'shared/components/Portal';
import { TOASTER_TYPES } from 'shared/components/Toast/constants';
import Message from 'shared/components/Wishlist/components/Message';
import CartNudge from 'shared/layouts/DetailsPage/components/CartNudge';

// api
import { addToWishlist, removeFromWishlist } from 'shared/api/wishlist';

// storage
import {
  addProductToWishlistOfStorage,
  removeProductFromWishlistOfStorage,
  removeStoredProductToBeAddedToWishlist,
  setProductToAddToWishlistAfterLogin,
} from 'shared/components/Wishlist/storage';

// constants
import {
  ADDED_TO_WISHLIST_MESSAGE,
  REMOVED_FROM_WISHLIST_MESSAGE,
  REMOVE_PRODUCT_FROM_WISHLIST_FAILED,
  ADD_PRODUCT_TO_WISHLIST_FAILED,
  MESSAGES,
} from './constants';
import { DEVICE_TYPE } from 'shared/api/wishlist';

// tracking
import {
  trackAddToWishlistClicked,
  trackLoginNudgeClosed,
  trackLoginSignupFromWishlist,
  trackRemoveFromWishlistClicked,
} from 'layouts/Wishlist/analytics';

// defs
interface IWishlist {
  wishlistEnabled: boolean;
  isLoggedInUser: boolean;
  isWishListProduct: boolean;
  size: number;
  redirectTo?: string;
  className: string;
  productId: string;
  displayWishlistMessage: boolean;
  showWishlistMessage?: (show: boolean, message: string) => void;
  addProductToWishlistStore: (productId: string) => void;
  removeProductFromWishlistStore: (productId: string) => void;
  updateShowAnimationForProductId: (productId: string) => void;
  showAnimationForProductId: string;
  price: number;
  isOutOfStock: boolean;
  canShowNotification?: boolean;
}

let TOAST_TIMEOUT: Timeout | null = null;
const TOAST_TIME = 3000;

function Wishlist({
  wishlistEnabled,
  isLoggedInUser,
  redirectTo,
  size,
  className,
  productId,
  isWishListProduct,
  displayWishlistMessage = false,
  addProductToWishlistStore,
  removeProductFromWishlistStore,
  showAnimationForProductId,
  updateShowAnimationForProductId,
  price,
  isOutOfStock,
  canShowNotification,
}: IWishlist) {
  const [shouldShowLoginNudge, setShowLoginNudge] = useState(false);

  const addOrRemoveMessage = isWishListProduct
    ? ADDED_TO_WISHLIST_MESSAGE
    : REMOVED_FROM_WISHLIST_MESSAGE;

  const [showMessage, setShowMessage] = useState(false);

  const showLoginNudge = () => setShowLoginNudge(true);
  const hideLoginNudge = () => {
    setShowLoginNudge(false);
    removeStoredProductToBeAddedToWishlist();
    trackLoginNudgeClosed();
  };
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState(ADD_PRODUCT_TO_WISHLIST_FAILED);

  // Cart Nudge Toast used on PDP
  const [noticationToast, setNotificationToast] = useState(false);
  const [notifcationToastMessage, setNotifcationToastMessage] = useState('');

  const handleToast = (message: string) => {
    // set default toast, then open a new toast
    // this is done for the animation effect
    setNotificationToast(false);
    setNotifcationToastMessage('');

    setNotificationToast(true);
    setNotifcationToastMessage(message);

    clearTimeout(TOAST_TIMEOUT);
    TOAST_TIMEOUT = setTimeout(() => {
      setNotificationToast(false);
      setNotifcationToastMessage('');
    }, TOAST_TIME);
  };

  const onClickToast = () => {
    // on click of the toast, close it
    clearTimeout(TOAST_TIMEOUT);
    setNotificationToast(false);
    setNotifcationToastMessage('');
  };

  const addProductToWishlist = () => {
    // update store wishlist
    addProductToWishlistStore(productId);

    // display message at bottom of the product
    setShowMessage(true);

    addToWishlist({ itemId: productId, deviceType: DEVICE_TYPE.MOBILE })
      .then(() => {
        // update wishlist in storage
        addProductToWishlistOfStorage(productId);

        // track adding product to wishlist
        trackAddToWishlistClicked(productId, price, isOutOfStock);

        // show toast
        canShowNotification && handleToast(MESSAGES.ADD);
      })
      .catch(() => {
        // reverting the changes, due to api failure;
        setShowMessage(false);
        removeProductFromWishlistStore(productId);

        // toast for failure
        setToastMessage(ADD_PRODUCT_TO_WISHLIST_FAILED);
        setShowToast(true);
      });
  };

  const removeProductFromWishlist = () => {
    // update store wislist
    removeProductFromWishlistStore(productId);

    // display message at bottom of the product
    setShowMessage(true);

    removeFromWishlist({ itemId: productId, deviceType: DEVICE_TYPE.MOBILE })
      .then(() => {
        // update wishlist in storage
        removeProductFromWishlistOfStorage(productId);

        // track removing product from wishlist
        trackRemoveFromWishlistClicked(productId, price, isOutOfStock);

        // show toast
        canShowNotification && handleToast(MESSAGES.REMOVE);
      })
      .catch(() => {
        // reverting the changes, due to api failure;
        addProductToWishlistStore(productId);

        // toast for failure
        setToastMessage(REMOVE_PRODUCT_FROM_WISHLIST_FAILED);
        setShowToast(true);
      });
  };

  const addOrRemoveProduct = () => {
    isWishListProduct ? removeProductFromWishlist() : addProductToWishlist();
  };

  const guestUserClicked = () => {
    setProductToAddToWishlistAfterLogin(productId);
    showLoginNudge();
    trackLoginSignupFromWishlist();
  };

  const handleOnWishlistClicked = (event: any) => {
    event.preventDefault();
    event.stopPropagation();
    isLoggedInUser ? addOrRemoveProduct() : guestUserClicked();
  };

  const onToastHide = () => {
    setShowToast(false);
  };

  const onMessageAnimationEnd = () => {
    setShowMessage(false);
  };

  useEffect(() => {
    if (showAnimationForProductId != productId) return;

    setShowMessage(true);
    updateShowAnimationForProductId('');
    trackAddToWishlistClicked(productId, price, isOutOfStock);
  }, [isOutOfStock, price, productId, showAnimationForProductId, updateShowAnimationForProductId]);

  if (wishlistEnabled === false) {
    return null;
  }

  return (
    <>
      <Container className={className}>
        {noticationToast && (
          <CartNudge content={notifcationToastMessage} onClick={onClickToast} actionText="" />
        )}

        <WishlistButton onClick={handleOnWishlistClicked} className={'circle'}>
          <WishlistIcon
            className={isWishListProduct ? 'icon-move-to-wishlist' : 'icon-heart'}
            isWishlistProduct={isWishListProduct}
            size={size}
          />
        </WishlistButton>
      </Container>

      {displayWishlistMessage && showMessage && (
        <Message
          onHide={onMessageAnimationEnd}
          message={addOrRemoveMessage}
          key={addOrRemoveMessage}
        />
      )}

      {showToast && (
        <Portal>
          <Toast type={TOASTER_TYPES.ERROR} message={toastMessage} onHide={onToastHide} />
        </Portal>
      )}

      {shouldShowLoginNudge && (
        <LoginNudgeContainer onClick={(e) => e.stopPropagation()}>
          <LoginNudge onNudgeHide={hideLoginNudge} redirectTo={redirectTo} />
        </LoginNudgeContainer>
      )}
    </>
  );
}

export default memo(Wishlist);
